import { useState } from "react";
import { Play, Sparkles, MapPin, Camera, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { videos, brandedPosters, galleryImages } from "../mock/mock";
import CTA from "../components/sections/CTA";

const zones = [
  {
    id: "prayer-hall",
    name: "Prayer & Shloka Hall",
    description: "A serene space where every day begins with morning prayer, Sanskrit shlokas and a moment of stillness.",
    image: "https://customer-assets-rejwkqb3.emergentagent.net/job_gayanam-preschool/artifacts/mdv1x5gr_WhatsApp%20Image%202026-07-15%20at%208.43.27%20PM%20%289%29.jpeg",
    highlights: ["Daily morning prayer", "Sanskrit shloka recitation", "Meditation mats", "Bal Saraswati corner"]
  },
  {
    id: "classroom",
    name: "Vibrant Classrooms",
    description: "Colorful, safe and stimulating classrooms designed with child-scale furniture and interactive learning zones.",
    image: "https://customer-assets-rejwkqb3.emergentagent.net/job_gayanam-preschool/artifacts/hyz9y3ig_WhatsApp%20Image%202026-07-15%20at%208.43.27%20PM%20%288%29.jpeg",
    highlights: ["Age-appropriate furniture", "Learning-by-play zones", "Sanskrit corner", "STEM & Art nooks"]
  },
  {
    id: "outdoor",
    name: "Outdoor Play Area",
    description: "Safe, spacious outdoor play zones with age-appropriate equipment and lots of green space.",
    image: "https://customer-assets-rejwkqb3.emergentagent.net/job_gayanam-preschool/artifacts/u4qlvdvv_WhatsApp%20Image%202026-07-15%20at%208.43.27%20PM%20%287%29.jpeg",
    highlights: ["Safe play equipment", "Sand & water play", "Mini garden", "Nature exploration"]
  },
  {
    id: "music",
    name: "Music & Movement Room",
    description: "A dedicated space for music, dance, storytelling and dramatic play — where creativity meets tradition.",
    image: "https://customer-assets-rejwkqb3.emergentagent.net/job_gayanam-preschool/artifacts/nc82ua9d_WhatsApp%20Image%202026-07-15%20at%208.43.27%20PM%20%2811%29.jpeg",
    highlights: ["Musical instruments", "Story circle", "Dance floor", "Dress-up corner"]
  },
  {
    id: "yoga",
    name: "Yoga & Wellness Studio",
    description: "A calm, natural-light-filled space for daily yoga, pranayam and mindful movement sessions.",
    image: "https://images.unsplash.com/photo-1587574293340-e0011c4e8ecf?w=1200",
    highlights: ["Kids yoga mats", "Pranayam corner", "Nature-inspired décor", "Certified instructor"]
  },
  {
    id: "library",
    name: "Story Library",
    description: "A cozy library corner filled with Panchatantra tales, Ramayana stories, picture books and Bharatiya heritage stories.",
    image: "https://images.pexels.com/photos/4145153/pexels-photo-4145153.jpeg?w=800",
    highlights: ["Panchatantra collection", "Ramayana stories", "Picture books", "Reading nooks"]
  }
];

const VirtualTourPage = () => {
  const [activeZone, setActiveZone] = useState(zones[0]);
  const tourVideo = videos.find((v) => v.id === "campus-tour") || videos[3];

  return (
    <div>
      {/* Hero */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-orange-100 mb-4">
            <Sparkles className="w-4 h-4 text-orange-600" />
            <span className="text-xs font-semibold text-orange-700 tracking-widest uppercase">Virtual Campus Tour</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-stone-800 mb-4">
            Step Inside <span className="text-orange-600">Aadi Gayanam</span>
          </h1>
          <p className="text-stone-600 max-w-2xl mx-auto">
            Explore our upcoming Jaipur campus — thoughtfully designed spaces where Sanatan values and modern learning come alive for our little ones.
          </p>
        </div>
      </section>

      {/* Featured video */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="aspect-video rounded-3xl overflow-hidden shadow-2xl border border-orange-100">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${tourVideo.youtubeId}?rel=0&modestbranding=1`}
                title="Aadi Gayanam Campus Tour"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="text-center mt-6">
              <p className="text-stone-600 text-sm">
                <MapPin className="inline w-4 h-4 text-orange-600 mr-1" />
                Aadi Gayanam Play School, Aarohi Welfare Foundation, Jaipur
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Zone explorer */}
      <section className="py-16 pattern-warm">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-800 mb-3">
              Explore Our <span className="text-orange-600">Learning Zones</span>
            </h2>
            <p className="text-stone-600 max-w-xl mx-auto">Six thoughtfully designed spaces, each nurturing a different aspect of your child's growth.</p>
          </div>

          <div className="grid lg:grid-cols-[300px_1fr] gap-6 max-w-6xl mx-auto">
            {/* Zone list */}
            <div className="space-y-2">
              {zones.map((z) => (
                <button
                  key={z.id}
                  onClick={() => setActiveZone(z)}
                  className={`w-full text-left p-4 rounded-2xl border transition-all ${
                    activeZone.id === z.id
                      ? "bg-white border-orange-300 shadow-md"
                      : "bg-white/60 border-orange-100 hover:bg-white hover:shadow-sm"
                  }`}
                >
                  <p className={`font-display font-bold ${activeZone.id === z.id ? "text-orange-700" : "text-stone-800"}`}>
                    {z.name}
                  </p>
                  <p className="text-xs text-stone-500 mt-0.5 line-clamp-2">{z.description}</p>
                </button>
              ))}
            </div>

            {/* Active zone preview */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-orange-100">
              <div className="relative aspect-[16/10] bg-stone-100">
                <img src={activeZone.image} alt={activeZone.name} className="w-full h-full object-cover" />
                <span className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm text-orange-700 text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider shadow">
                  {activeZone.name}
                </span>
              </div>
              <div className="p-6">
                <h3 className="font-display font-bold text-2xl text-stone-800 mb-2">{activeZone.name}</h3>
                <p className="text-stone-600 mb-5 leading-relaxed">{activeZone.description}</p>
                <div className="grid grid-cols-2 gap-2">
                  {activeZone.highlights.map((h, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-stone-700">
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0" />
                      <span>{h}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo gallery preview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-800 mb-3">
              A Glimpse of Life at <span className="text-orange-600">Aadi Gayanam</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {galleryImages.slice(0, 8).map((img) => (
              <div key={img.id} className="aspect-square rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all">
                <img src={img.url} alt={img.caption} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/gallery">
              <Button variant="outline" className="border-2 border-orange-600 text-orange-700 hover:bg-orange-50 rounded-full h-11 px-6">
                See Full Gallery <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <CTA />
    </div>
  );
};

export default VirtualTourPage;
